import { MatSort } from '@angular/material/sort';
import { TylerMatTableDataSource } from './../../shared-components/angular-material/extended.class';
import { detailExpand } from './../../shared-components/angular-material/mat-animations';
import { System } from './../../shared-components/System';
import { Component, OnInit, OnDestroy, EventEmitter, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { ProgramsAndServicesService } from './programs-and-services.service';
import { Subscription } from 'rxjs';
import { ProgramAndService } from './programs-and-services.model';
import { StudentService } from '../../shared-components/student/student.service';
import { Attachment } from '../../shared-components/models/attachment.model';
import { YearService } from '../../shared-components/year/year.service';
import { SharedService } from '../../shared-components/shared.service';
import { ProgramsAndServicesFilter } from './programs-and-services-filter';

@Component({
	selector: 'app-programs-and-services',
	templateUrl: './programs-and-services.component.html',
	styleUrls: [
		'./programs-and-services.component.css'
	],
	animations: [
		detailExpand
	]
})
export class ProgramsAndServicesComponent implements OnInit, OnDestroy {

	// Properties
	private _sort: MatSort;
	@ViewChild('filterMenu', { read: TemplateRef }) filterMenu: TemplateRef<any>;
	subscriptions: Subscription[] = [];
	programsAndServices: ProgramAndService[];
	tableData = new TylerMatTableDataSource();
	columns = ['More', 'ProgramName', 'ServiceName', 'EntryDate', 'ExitDate', 'Comments', 'Attachments'];
	junkDate = new Date('0001-01-01T00:00:00');
	toggleAllRows: EventEmitter<boolean> = new EventEmitter<boolean>();
	gridHeight = 0;
    expanded: { [key: number]: boolean } = {};
    showFilter: boolean = false;
	filter: ProgramsAndServicesFilter;
	previousFilter: ProgramsAndServicesFilter;

	// Class Functions
	constructor(
		private programsAndServicesService: ProgramsAndServicesService,
		private studentService: StudentService,
		private yearService: YearService,
		private sharedService: SharedService,
		private changeDetector: ChangeDetectorRef
	) { }

	get sort(): MatSort {
		return this._sort;
	}

	@ViewChild(MatSort) set sort(value: MatSort) {
		this._sort = value;
		if (this.tableData) {
			this.tableData.sort = this._sort;
		}
	}

	// Event Functions
	ngOnInit() {
		// POST: initializes and obtains the necessary data

		// Initializing data
		this.filter = new ProgramsAndServicesFilter();
		this.previousFilter = new ProgramsAndServicesFilter();
		this.filter.shown = 0;
		this.previousFilter.shown = 0;
		if (this.studentService.selectedProgramName && this.studentService.selectedServiceName) {
			this.filter.programName = this.studentService.selectedProgramName;
			this.filter.serviceName = this.studentService.selectedServiceName;

			this.studentService.selectedProgramName = '';
			this.studentService.selectedServiceName = '';
		}

		// Getting data
		this.getProgramsAndServices();
		this.subscriptions.push(
			this.studentService.selectedStudentStream$.subscribe(() => {
				this.getProgramsAndServices();
			}),
			this.toggleAllRows.subscribe(toggle => {
				this.tableData.expandAll(toggle);
			}),
			this.yearService.observableStudent360YearStream$.subscribe(() => {
				this.getProgramsAndServices();
			}),
			this.studentService.observableSelectedProgramAndServiceStream.subscribe(() => {
				if (this.studentService.selectedProgramName && this.studentService.selectedServiceName) {
					this.filter.programName = this.studentService.selectedProgramName;
					this.filter.serviceName = this.studentService.selectedServiceName;

					this.studentService.selectedProgramName = '';
					this.studentService.selectedServiceName = '';

					this.tableData.removeAllFilters();
					this.applyFilter();
					this.changeDetector.detectChanges();
				}
			})
		);
		this.tableData.sort = this.sort;
	}

	ngOnDestroy() {
		// POST: ends all subscriptions
		this.subscriptions.forEach(subscription => {
			subscription.unsubscribe();
		});
	}

	openFilter() {
        this.showFilter = !this.showFilter;
	}

	clearFilter() {
		// PRE: the user clicks on the clear button in the filter
		// POST: clears the filter data and DOES NOT CLOSE THE FILTER
		let getNewData = false;
		if (this.filter.shown != new ProgramsAndServicesFilter().shown) {
			getNewData = true;
		}
		Object.assign(this.filter, new ProgramsAndServicesFilter());
		if (getNewData) {
			this.getProgramsAndServices();
		} else {
		    this.applyFilter();
        }
		this.changeDetector.detectChanges();
	}

	clearFilterFromNotice() {
		// PRE: the user clicks the clear filter link from the notice on the footer
		// POTS: clears the footer and resets ALL of the values (because the filter isn't open)
		let getNewData = false;
		if (this.filter.shown != new ProgramsAndServicesFilter().shown) {
			getNewData = true;
		}
		Object.assign(this.filter, new ProgramsAndServicesFilter());
        this.tableData.removeAllFilters();
		if (getNewData) {
			this.getProgramsAndServices();
		} else {
            this.applyFilter();
        }
		this.changeDetector.detectChanges();
	}

	closeFilter() {
        this.openFilter();
	}

	// Methods
	isFiltered() {
		// POST: returns true if the data is being panelFiltered
		if (this.filter.entryDateStart && this.filter.entryDateStart.toString() == 'Invalid Date') {
			this.filter.entryDateStart = undefined;
		}
		if (this.filter.entryDateEnd && this.filter.entryDateEnd.toString() == 'Invalid Date') {
		    this.filter.entryDateEnd = undefined;
        }
		if (this.filter.exitDateStart && this.filter.exitDateStart.toString() == 'Invalid Date') {
			this.filter.exitDateStart = undefined;
		}
		if (this.filter.exitDateEnd && this.filter.exitDateEnd.toString() == 'Invalid Date') {
		    this.filter.exitDateEnd = undefined;
        }
		return !System.isEqual(this.filter, new ProgramsAndServicesFilter()) || this.tableData.isFiltered();
	}

	toggleExpanded(rowId: number) {
		// POST: toggles the expansion of that row
		this.expanded[rowId] = !this.expanded[rowId];
	}

	getProgramsAndServices() {
		// POST: uses the services to get programs and services, returning the observable
		this.subscriptions.push(
			this.programsAndServicesService.getProgramsAndServices(this.filter.shown).subscribe(programsAndServices => {
				this.programsAndServices = [];
				programsAndServices.forEach(programAndService => {
					for (let i = 0; i < programAndService.Attachments.length; i++) {
						programAndService.Attachments[i] = System.convert<Attachment>(programAndService.Attachments[i], new Attachment());
					}
					this.programsAndServices.push(System.convert<ProgramAndService>(programAndService, new ProgramAndService()));
				});
				setTimeout(
					() => {
						this.tableData.data = this.programsAndServices;
						this.applyFilter();
						this.expanded = {};
						if (this.sort) {
							this.tableData.sort = this.sort;
						}
					},
					0
				);
			})
		);
	}

	viewAttachment(attachment: Attachment) {
		// PRE: an attachment to view
		// POST: goes to view that attachment
		if (attachment.DocumentMimeType == 'application/vnd.openxmlformats-officedocument.word') {
			System.saveFile(attachment.DocumentBase64, attachment.DocumentMimeType, attachment.DocumentName);
		} else {
			System.viewFileFromId(attachment.AttachmentId, attachment.AttachmentFileName);
		}
	}

	hasDetails(programAndService: ProgramAndService): boolean {
		// POST: returns true if the program and service has details
		return programAndService.Comments != '' ||
			(programAndService.Attachments && programAndService.Attachments.length > 0) ||
			(programAndService.ProgramAndServicesDetail && programAndService.ProgramAndServicesDetail.length > 0);
	}

	applyFilter() {
		// POST: applies the filter to the data
		let data = this.programsAndServices;

		// Entry Date
		if (this.filter.entryDateStart && this.filter.entryDateStart.toString() != 'Invalid Date') {
		    System.roundDateDown(this.filter.entryDateStart);
			data = data.filter(datum =>
				datum.EntryDate >= this.filter.entryDateStart
			);
		}
		if (this.filter.entryDateEnd && this.filter.entryDateEnd.toString() != 'Invalid Date') {
		    System.roundDateUp(this.filter.entryDateEnd);
            data = data.filter(datum =>
                datum.EntryDate <= this.filter.entryDateEnd
            );
        }

		// Exit Date
		if (this.filter.exitDateStart && this.filter.exitDateStart.toString() != 'Invalid Date') {
		    System.roundDateDown(this.filter.exitDateStart);
			data = data.filter(datum =>
				datum.ExitDate >= this.filter.exitDateStart
			);
		}
		if (this.filter.exitDateEnd && this.filter.exitDateEnd.toString() != 'Invalid Date') {
            System.roundDateUp(this.filter.exitDateEnd);
            data = data.filter(datum =>
                datum.ExitDate <= this.filter.exitDateEnd
            );
        }

		// Program Name
		if (this.filter.programName) {
			data = data.filter(datum =>
				datum.ProgramName.toLowerCase().includes(this.filter.programName.toLowerCase())
			);
		}

		// Service Name
		if (this.filter.serviceName) {
			data = data.filter(datum =>
				datum.ServiceName.toLowerCase().includes(this.filter.serviceName.toLowerCase())
			);
		}

		// NOTE: Show is handled by getProgramsAndServices

		// Setting the table data
		this.tableData.data = data;
	}

	// These were created from a misunderstanding of ARIA but have been kept in in case they could become useful
	programAndServiceAriaLabel(programAndService: ProgramAndService): string {
		// PRE: a program and service to generate an aria label for
		// POST: creates the aria label for a program and service's table row
		return 'Program ' + programAndService.ProgramName + ' and service ' + programAndService.ServiceName + '. ' +
			'Entry date ' + programAndService.EntryDate.toDateString() +
			(programAndService.ExitDate.toDateString() != this.junkDate.toDateString() ? ' and exit date ' + programAndService.ExitDate.toDateString() + '. ' : '. ') +
			(programAndService.Comments.length > 0 ? 'Service has comments. ' : 'Service does not have comments. ') +
			(programAndService.Attachments.length > 0 ? 'Service has attachments. ' : 'Service does not have attachments. ') +
			(this.hasDetails(programAndService) ? 'Press enter to see details.' : '');
	}

	programAndServiceDetailAttachmentsAriaLabel(attachment: Attachment): string {
		// PRE: a program and service detail to generate an aria label for
		// POST: creates the aria label for the program and service detail's attachment's table row
		return 'Attachment ' + attachment.AttachmentFileName + '. ' +
			'Attachment Description: ' + attachment.AttachmentDescription + '. ' +
			'Attachment Added on: ' + attachment.AttachmentAddedOn.toDateString() + '. ' +
			'Attachment Added by: ' + attachment.AttachmentAddedBy + '. ' +
			'Press enter to view attachment.';
	}
}
